/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { getLocalizedPath } from 'helpers/getLocale';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import LinearProgress from 'components/LinearProgress';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from 'assets/icons/search_icon.svg?react';
import useDataListFactory from 'services/dataList';
import professionEndPoint from 'endPoints/professions';
import { getTagsList, getSkillsList, getCategories } from 'actions/profession';
import parseQS from 'helpers/parseQS';
import arrayUnique from 'helpers/arrayUnique';
import { ADSENSE_ACTIVE, COLUMNS_LIMIT_DEFAULT, IS_SNAP, SNAP_SHOT_LIMIT } from 'variables/main';
import AdSense from 'components/AdSense';
import Filters from './components/Filters';
import ChosenFilters from './components/ChosenFilters';
import SortComponent from './components/Sort';

const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));
const Helmet = React.lazy(() => import('components/Helmet'));
const Drawer = React.lazy(() => import('components/Drawer'));
const Cards = React.lazy(() => import('components/Card'));
import CardGridSkeleton from 'components/CardGridSkeleton';
const Header = React.lazy(() => import('components/Header'));
const Footer = React.lazy(() => import('components/Footer'));
const MainLayout = React.lazy(() => import('layouts/MainLayout'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Background = styled('section')(({ theme }) => ({
  background: `linear-gradient(to bottom, ${theme.palette.grey[50]}, ${theme.palette.white.main})`
}));

const HomeProfessionsWrapper = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  ...theme.wrapper,
  boxSizing: 'border-box',
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: 40,
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: theme.palette.textPrimary.main,
  [theme.breakpoints.down('lg')]: {
    display: 'block'
  },
  [theme.breakpoints.down('md')]: {
    fontSize: 36,
    lineHeight: '41.4px',
    marginBottom: 20
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 28,
    lineHeight: '36.4px',
    marginBottom: 25
  }
}));

const ActionsWrapper = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 10
}));

const TextFieldStyled = styled(TextField)(() => ({
  borderRadius: 8,
  '& .MuiOutlinedInput-root': {
    height: 44
  },
  '& .MuiOutlinedInput-input': {
    fontSize: 14,
    paddingTop: 0,
    paddingBottom: 0
  }
}));

const FilterIconWrapper = styled('div')(() => ({
  width: 42,
  height: 42,
  marginRight: 10,
  borderRadius: 8,
  backgroundColor: '#B9F833',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  flexShrink: 0,
  color: '#ffffff'
}));

const FilterIconStyled = styled(TuneIcon)(() => ({
  width: 24,
  height: 24
}));

const ButtonStyled = styled(Button)(() => ({
  display: 'block',
  margin: '0 auto',
  fontSize: 16
}));

const AdSenseWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  padding: '40px 100px',
  boxSizing: 'border-box',
  minHeight: 0,
  [theme.breakpoints.down('lg')]: {
    padding: '40px 50px'
  },
  [theme.breakpoints.down('md')]: {
    padding: '30px 30px'
  },
  [theme.breakpoints.down('sm')]: {
    padding: '24px 20px'
  },
  '& .adsbygoogle': {
    display: 'block',
    margin: '0 auto'
  }
}));

const ClearIconStyled = styled(ClearIcon)(({ theme }) => ({
  cursor: 'pointer',
  marginRight: 10,
  color: theme.palette.textPrimary.main
}));

const Description = styled(Typography)(() => ({
  marginBottom: 40,
  fontSIze: 21,
  lineHeight: '30px'
}));

const Professions = () => {
  const location = useLocation();
  const [urlFilters, setUrlFilters] = React.useState(parseQS(location.search));
  const sortRef = React.useRef(null);

  const handleSearchFocus = React.useCallback(() => {
    sortRef.current?.collapse();
  }, []);

  const [qsPage] = React.useState(() => {
    if (!IS_SNAP) return null;
    const pageNumber = (location?.pathname || '').split('/').pop() || null;
    if (pageNumber && !Number.isNaN(Number(pageNumber))) {
      return Number(pageNumber);
    }
    return null;
  });

  const { actions, data, loading, page, perPage, count, filters, sort } = useDataList(
    professionEndPoint,
    {
      page: qsPage || 1,
      perPage: IS_SNAP ? SNAP_SHOT_LIMIT : COLUMNS_LIMIT_DEFAULT,
      sort: { popularity: 'desc' },
      filters: {
        tag_ids: [urlFilters.tag_ids],
        skill_ids: [urlFilters.skill_ids],
        id: urlFilters.id
      }
    }
  );

  const [open, setOpen] = React.useState(false);
  const [tags, setTags] = React.useState(null);
  const [skills, setSkills] = React.useState(null);
  const [categories, setCategories] = React.useState(null);

  const tagsStated = useSelector((state) => state.profession.tags);
  const skillsStated = useSelector((state) => state.profession.skills);
  const categoriesStated = useSelector((state) => state.profession.categories);

  const dispatch = useDispatch();

  const t = useTranslate('Professions');

  const handleOpenDrawer = React.useCallback(() => setOpen(true), []);

  const handleCLoseDrawer = React.useCallback(() => setOpen(false), []);

  const handleFiltersChange = React.useCallback(
    (newFilters) => {
      actions.clearData();
      actions.onFilterChange({
        ...filters,
        ...newFilters
      });
    },
    [actions, filters]
  );

  const handleChangeCategories = React.useCallback(
    (ids, action) => {
      const categoriesList = filters?.category_ids || [];

      const categoriesIds =
        action === 'delete'
          ? categoriesList.filter((categoryId) => !ids.includes(categoryId))
          : ids;

      handleFiltersChange({
        category_ids: arrayUnique(categoriesIds)
      });
    },
    [filters, handleFiltersChange]
  );

  const handleChangeTags = React.useCallback(
    (ids, action) => {
      const tagsList = filters?.tag_ids || [];

      const tagIds = action === 'delete' ? tagsList.filter((tagId) => !ids.includes(tagId)) : ids;

      handleFiltersChange({
        tag_ids: arrayUnique(tagIds)
      });
    },
    [filters, handleFiltersChange]
  );

  const handleChangeSkills = React.useCallback(
    (ids, action) => {
      const skillsList = filters?.skill_ids || [];

      const skillIds =
        action === 'delete' ? skillsList.filter((skillId) => !ids.includes(skillId)) : ids;

      handleFiltersChange({
        skill_ids: arrayUnique(skillIds)
      });
    },
    [filters, handleFiltersChange]
  );

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const handleChangeSearch = React.useCallback(
    (e) => {
      actions.onSearchChange(e.target.value);
    },
    [actions]
  );

  const onKeyDown = React.useCallback(
    (e) => {
      if (e.key === 'Enter') {
        actions.onSearchChange(e.target.value, true, true);
        e.preventDefault();
      }
    },
    [actions]
  );

  const handleClearSearch = React.useCallback(() => {
    actions.clearData(true);
  }, [actions]);

  React.useEffect(() => {
    // Skip API calls during react-snap prerendering to prevent timeouts
    if (IS_SNAP) return;
    
    const fetchFilters = async () => {
      if (tagsStated) {
        setTags(tagsStated);
      } else {
        dispatch(getTagsList()).then((data) => {
          setTags(data);
        });
      }

      if (skillsStated) {
        setSkills(skillsStated);
      } else {
        dispatch(getSkillsList()).then((data) => {
          setSkills(data);
        });
      }

      if (categoriesStated) {
        setCategories(categoriesStated);
      } else {
        dispatch(getCategories())
          .then((data) => {
            setCategories(data);
          })
          .catch(() => {
            // getCategories re-throws on failure; swallow it here so it doesn't
            // become an unhandled rejection. chosenCategories guards non-arrays.
            setCategories([]);
          });
      }
    };

    fetchFilters();
  }, [dispatch]);

  const handleClearPersonal = React.useCallback(() => {
    setUrlFilters({});
    actions.clearFilters();
  }, [actions]);

  const chosenTags = React.useMemo(() => {
    if (!Array.isArray(tags) || !filters?.tag_ids) {
      return [];
    }
    return (filters?.tag_ids || [])
      .map((tagId) => tags.find((tag) => tag._id === tagId))
      .filter(Boolean);
  }, [tags, filters]);

  const chosenSkills = React.useMemo(() => {
    if (!Array.isArray(skills) || !filters?.skill_ids) {
      return [];
    }
    return (filters?.skill_ids || [])
      .map((skillId) => skills.find((skill) => skill._id === skillId))
      .filter(Boolean);
  }, [skills, filters]);

  const chosenCategories = React.useMemo(() => {
    if (!Array.isArray(categories) || !filters?.category_ids) {
      return [];
    }
    return (filters?.category_ids || [])
      .map((categoryId) => categories.find((category) => category._id === categoryId))
      .filter(Boolean);
  }, [categories, filters]);

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const breadcrumbs = React.useMemo(() => {
    return [
      {
        label: 'Home',
        link: '/'
      },
      {
        label: 'Professions'
      }
    ];
  }, []);

  return (
    <>
      <Helmet page={'professions'} includeHreflang={true} />
      <MainLayout>
        <Header />
        <Background>
          <HomeProfessionsWrapper>
            <Breadcrumbs breadcrumbs={breadcrumbs} />

            <Title variant="h1">{t('Professions')}</Title>

            <Description>{t('IntroText')}</Description>

            <ActionsWrapper>
              <FilterIconWrapper onClick={handleOpenDrawer}>
                <FilterIconStyled />
              </FilterIconWrapper>
              <SortComponent ref={sortRef} sort={sort} actions={actions} />
              <TextFieldStyled
                value={filters?.name || ''}
                onChange={handleChangeSearch}
                onKeyDown={onKeyDown}
                onFocus={handleSearchFocus}
                onMouseDown={handleSearchFocus}
                placeholder={t('Search')}
                variant="outlined"
                fullWidth
                slotProps={{
                  htmlInput: {
                    'aria-label': t('Search')
                  },
                  input: {
                    endAdornment: (
                      <>
                        {filters?.name ? <ClearIconStyled onClick={handleClearSearch} /> : null}
                        <SearchIcon />
                      </>
                    )
                  }
                }}
              />
            </ActionsWrapper>

            <ChosenFilters
              chosenCategories={chosenCategories}
              chosenTags={chosenTags}
              chosenSkills={chosenSkills}
              filters={filters}
              personal={urlFilters.id}
              handleChangeCategories={handleChangeCategories}
              handleChangeTags={handleChangeTags}
              handleChangeSkills={handleChangeSkills}
              handleClearPersonal={handleClearPersonal}
              t={t}
            />

            {urlFilters.id ? <Description>{t('PersonalizesProfessions')}</Description> : null}

            {!data && (loading || !data) ? (
              <CardGridSkeleton count={6} />
            ) : (
              <Cards list={data} />
            )}

            {data && data.length === 0 ? <Typography>{t('NoData')}</Typography> : null}

            {loading || !data ? <LinearProgress /> : null}

            {limitReached ? null : (
              <>
                <ButtonStyled
                  variant="contained"
                  color="primary"
                  onClick={handleChangePage}
                  aria-label={t('LoadMore')}>
                  {t('LoadMore')}
                </ButtonStyled>
                <Link
                  to={getLocalizedPath(`/professions/page/${page + 1}`)}
                  style={{ display: 'none' }}
                  target="_blank"
                  rel="noopener noreferrer">
                  {t('LoadMore')}
                </Link>
              </>
            )}
          </HomeProfessionsWrapper>
        </Background>

        {ADSENSE_ACTIVE && (
          <AdSenseWrapper>
            <AdSense
              adSlot="6193607988"
              adLayoutKey="-7f+f1-1o-4h+ei"
              style={{ display: 'block' }}
            />
          </AdSenseWrapper>
        )}

        <Footer />

        <Drawer anchor={'left'} open={open} onClose={handleCLoseDrawer}>
          <Filters
            categories={categories}
            tags={tags}
            skills={skills}
            filters={filters}
            actions={actions}
            handleChangeCategories={handleChangeCategories}
            handleChangeTags={handleChangeTags}
            handleChangeSkills={handleChangeSkills}
            t={t}
          />
        </Drawer>
      </MainLayout>
    </>
  );
};

export default Professions;
